import { useEffect, useState, useRef } from 'react';
import ConfirmModal from '@components/ConfirmModal';
import { WithTranslation, withTranslation } from 'react-i18next';
import { TimerIcon } from '@icons/TimerIcon';
import CountdownTimer from './CountdownTimer';
import { Box } from '@mui/material';

import { useIdleTimer, IIdleTimer } from 'react-idle-timer';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import {
  defaultIdleTimeout,
  defaultIdleCountdownTimeout,
} from '@constants/idleTimeout';
import { env } from 'env';

const { REACT_APP_FULL_TIME_EVENT_TAGS } = env;

const TAGS = REACT_APP_FULL_TIME_EVENT_TAGS?.split(',').map((x) =>
  x.toLowerCase(),
);

import { useStyles } from './styles';

export interface IIdleTimerModal extends WithTranslation {}

const IdleTimerWithModal = ({ t: translate }: IIdleTimerModal) => {
  const { currentEvent, isUserEnteringEvent } = useMytaverseEvent();
  const { logoutUser } = useMytaverse();

  const [isIdleDisabled, setIsDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const idleTimerInstanceRef = useRef<IIdleTimer | null>(null);

  const classes = useStyles();

  const idleTimerSettings =
    currentEvent?.idleTimer ?? currentEvent?.world?.settings?.idleTimer;
  const isIdleTimerEnabled = currentEvent?.idleTimer?.enabled ?? true;

  useEffect(() => {
    // keep it for backward compatibility
    if (
      isUserEnteringEvent &&
      currentEvent?.eventTags &&
      TAGS?.length &&
      !Object.keys(currentEvent?.idleTimer || {}).length
    ) {
      const tags = currentEvent?.eventTags.filter((tag) => {
        return TAGS.includes(tag.toLowerCase());
      });

      if (tags?.length <= 0) {
        setIsDisabled(true);
        idleTimerInstanceRef.current?.pause();
      }
    } else if (isUserEnteringEvent && !isIdleTimerEnabled) {
      setIsDisabled(true);
      idleTimerInstanceRef.current?.pause();
    }
  }, [
    currentEvent?.idleTimer?.enabled,
    isUserEnteringEvent,
    idleTimerInstanceRef.current,
  ]);

  idleTimerInstanceRef.current = useIdleTimer({
    timeout: idleTimerSettings?.timeout
      ? idleTimerSettings?.timeout * 1000
      : defaultIdleTimeout * 1000,
    onIdle: () => {
      setIsModalOpen(true);

      idleTimerInstanceRef.current?.pause();
    },
    element: document,
    disabled: isIdleDisabled,
  });

  const idleTimerOnConfirm = () => {
    setIsModalOpen(false);
    idleTimerInstanceRef.current?.reset();
  };

  const onCountdownFinish = () => {
    logoutUser();
  };

  return (
    <>
      <ConfirmModal
        altStyling
        hideCloseButton={true}
        title={translate('idleTimerModal.title')}
        message={translate('idleTimerModal.message')}
        onConfirm={idleTimerOnConfirm}
        open={isModalOpen}
        confirmButtonName={translate('idleTimerModal.continue')}
        sx={{
          actions: classes.actions,
        }}
      >
        <Box sx={classes.timer}>
          <TimerIcon />
          <CountdownTimer
            sx={classes.timer.countdownText}
            onCountdownFinish={onCountdownFinish}
            seconds={
              idleTimerSettings?.counterTimeout ?? defaultIdleCountdownTimeout
            }
          />
        </Box>
      </ConfirmModal>
    </>
  );
};

export default withTranslation()(IdleTimerWithModal);
